import * as React from 'react'

import { Avatar, Box, Button } from '@factorialco/deprecated-design-system'
import { AnimatePresence, motion } from 'framer-motion'
import { CommentEditorDocument, MentionableAccessesDocument } from 'generated/resources/graphql'

import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import { MentionedAccess } from 'modules/core/components/TextEditor/plugins/DebouncedMentionsPlugin'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import { useLazyQuery, useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { sanitizeHtml } from 'modules/contracts/lib/sanitizeHtml'

export interface CommentsCommentValues {
  authorId: number
  companyId: number
  content: string
  resourceId: number
  mentionedAccessIds?: number[]
}

interface Props {
  resourceId: number
  onAddComment: (
    variables: CommentsCommentValues
  ) => Promise<UnhandledResult<Result<Omit<unknown, 'errors'>>>>
  onSubmit?: () => void
  onFocus?: () => void
  onFetchCustomMentionableAccesses?: (queryString: string) => Promise<MentionedAccess[]> | undefined
}

const CommentEditor = ({
  resourceId,
  onAddComment,
  onSubmit,
  onFocus,
  onFetchCustomMentionableAccesses,
}: Props) => {
  const { access, company } = useCurrentSession()
  const editorRef = React.useRef<EditorHandle>(null)
  const [isActive, setIsActive] = React.useState(false)
  const [mentionedAccessIds, setMentionedAccessIds] = React.useState<number[] | undefined>([])

  const { data: commentAccessData } = useQuery(CommentEditorDocument, {
    variables: { authorId: access.id },
  })

  const [fetchAllActiveAccesses] = useLazyQuery(MentionableAccessesDocument)

  const handleMentionQueryStringChanged = async (
    queryString: string
  ): Promise<MentionedAccess[]> => {
    if (onFetchCustomMentionableAccesses) {
      return (await onFetchCustomMentionableAccesses(queryString)) ?? []
    }
    const result = await fetchAllActiveAccesses({ variables: { startsWith: queryString } })
    return (
      result.data?.apiCore.accessesConnection.nodes.map((a) => ({
        id: a.id,
        fullName: a.fullName,
        avatar: a.avatar ?? '',
      })) ?? []
    )
  }

  const avatar = commentAccessData?.apiCore.access?.avatar

  useUnsafeEffect(() => {
    if (isActive) {
      editorRef.current?.focus()
    }
  }, [isActive])

  const handleActiveChanged = (active: boolean) => {
    setIsActive(active)

    if (active) {
      onFocus?.()
    }
  }

  const form = useForm<CommentsCommentValues>({
    defaultValues: {
      authorId: access.id,
      companyId: company.id,
      content: '',
      resourceId: resourceId,
    },
    onSubmit: async (data) => {
      const result = await onAddComment({
        authorId: access.id,
        companyId: company.id,
        content: data.content,
        resourceId: resourceId,
        mentionedAccessIds: mentionedAccessIds,
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      })

      if (handled.success) {
        editorRef?.current?.clear()
        onSubmit?.()
      }
      return handled
    },
  })

  return (
    <Box width="full" paddingX="s24">
      <AnimatePresence mode={isActive ? 'sync' : 'wait'}>
        {isActive ? (
          <>
            <motion.div
              key="texteditor"
              style={{ overflow: 'hidden' }}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0, transition: { ease: 'easeOut', duration: 0.15 } }}
              transition={{ ease: 'easeOut', duration: 0.2 }}
              onAnimationComplete={onFocus}
            >
              <Box flexDirection="row" gap="s8">
                <Avatar
                  url={buildUrl(avatar)}
                  label={commentAccessData?.apiCore.access?.fullName ?? ''}
                  size="s40"
                />
                <Box width="full">
                  <TextEditor
                    onChange={(data) => {
                      if (data && typeof data !== 'string') {
                        form.setFieldValue('content', data.value)
                        setMentionedAccessIds(data?.ids)
                      }
                    }}
                    placeholder={i18n.t('comments.editor.placeholder_detail')}
                    initialEditorState={{ data: '' }}
                    ref={editorRef}
                    enableBetaFeatures={false}
                    hasDebouncedMentions
                    onMentionQueryStringChanged={handleMentionQueryStringChanged}
                  />
                  <Box flexDirection="row" gap="s8" paddingTop="s16">
                    <Button
                      type="secondary"
                      onClick={() => {
                        form.setValues({ ...form.values, content: '' })
                        handleActiveChanged(false)
                      }}
                    >
                      {i18n.t('comments.actions.cancel')}
                    </Button>
                    <Button
                      type="primary"
                      onClick={() => form.handleSubmit()}
                      disabled={
                        form.isSubmitting ||
                        !form.isValid ||
                        !form.values.content ||
                        (/>\s+</.test(form.values.content) && !mentionedAccessIds?.length) ||
                        sanitizeHtml(form.values.content) === ''
                      }
                    >
                      {i18n.t('comments.actions.comment')}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </>
        ) : (
          <motion.div
            key="texteditor"
            initial={{ height: 44, opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ ease: 'easeOut', duration: 0.2 }}
          >
            <Box flexDirection="row" gap="s8" alignItems="center">
              <Avatar
                url={buildUrl(avatar)}
                label={commentAccessData?.apiCore.access?.fullName ?? ''}
                size="s40"
              />
              <TextField
                placeholder={i18n.t('comments.editor.placeholder')}
                onClick={() => {
                  handleActiveChanged(true)
                }}
              />
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}

export default CommentEditor
